import { text } from "express";
import { CSVLexer } from "../../../../library-js/text/lexer/CSVLexer";
import { LexerMatcherLibrary } from "../../../../library-js/text/lexer/LexerMatcherLibrary";
import { readFileAsText } from "../../html/dom/readFile";

export class CSVIndex
{
  line:number;
  row:number;

  get hash()
  {
    return this.line + "|" + this.row;
  }

  set( line:number, row:number )
  {
    this.line =line;
    this.row = row;
  }

  static $( line:number, row:number )
  {
    let c = new CSVIndex();
    c.set( line, row );
    return c;
  }

  get nextLine():CSVIndex
  {
    return CSVIndex.$( this.line + 1, this.row );
  }

  get nextRow():CSVIndex
  {
    return CSVIndex.$( this.line, this.row + 1);
  }

  get previousRow():CSVIndex
  {
    return CSVIndex.$( this.line, this.row - 1);
  }
}

export class CSVRange
{
  start:CSVIndex;
  end:CSVIndex;

  static create( start:CSVIndex, end:CSVIndex )
  {
    let r = new CSVRange();
    r.start = start;
    r.end   = end;
    return r;
  }
}

export class CSV
{
  lines:string[][] = [];
  filePath:string;
  _error = false;

  get lastLineIndex()
  {
    return this.lines.length - 1;
  }

  appendLine( line:string[] )
  {
    this.lines.push( line );
  }

  static with( header:string[], numLines:number = 0, line:string[] = null ):CSV
  {
    if ( ! header && ! line )
    {
      console.error( "No header or line defined" );
      return null;
    }

    if ( header && line && ( header.length != line.length ) )
    {
      console.error( "Header/Line mismatch", header, line );
      return null;
    }

    let csv = new CSV();

    if ( header )
    {
      csv.lines.push( header );
    }

    if ( ! line )
    {
      line = [];

      for ( let i = 0; i < header.length; i++ )
      {
        line.push( "" );
      }      
    }

    for ( let i = 0; i < numLines; i++ )
    {
      let lineCopy = [].concat( line );
      csv.lines.push( lineCopy );
    }

    return csv;
  }

  static fromText( text:string, clear:boolean = true ):CSV
  {
    let csv = new CSV();
    csv.read( text );

    if ( clear )
    {
      csv.clearEmpty();
    }

    return csv;

  }

  serialize()
  {
    let lines:string[] = [];

    for ( let i = 0; i < this.numLines; i++ )
    {
      lines.push( this._serializeLine( i ) );
    }

    return lines.join( "\n" );
  }

  _serializeLine( lineIndex:number )
  {
    let line = this.getLine( lineIndex );

    let outputFragments:string[] = [];
    
    for ( let i = 0; i < line.length; i++ )
    {
      let value = line[ i ];

      if ( CSV.needsEscaping( value ) )
      {
        value = CSV.escapeText( value );
        value = "\"" + value + "\"";
      }

      outputFragments.push( value );
    }

    return outputFragments.join( "," );
  }

  static escapeCharacters = ",\\\"";
  static escapeCharacterReplacements = 
  [
    ",", "\\\\", "\\\""
  ];

  static needsEscaping( text:string )
  {
    for ( let i = 0; i < text.length; i++ )
    {
      let escapeIndex = CSV.escapeCharacters.indexOf( text[ i ] );

      if ( escapeIndex != -1 )
      {
        return true;
      }

    }

    return false;
  }

  static escapeText( text:string )
  {
    let value = "";
    
    for ( let i = 0; i < text.length; i++ )
    {
      let escapeIndex = CSV.escapeCharacters.indexOf( text[ i ] );

      if ( escapeIndex == -1 )
      {
        value += text[ i ];
      }
      else
      {
        value += CSV.escapeCharacterReplacements[ escapeIndex ];
      }
    }

    return value;
  }

  static unescapeText( text:string )
  {
    text = text.replace( /\\\"/g, "\"" );
    text = text.replace( /\\\\/g, "\\" );

    return text;
  }



  static async fromFile( file:File, clear:boolean = true ):Promise<CSV>
  {
    let text = await readFileAsText( file );
    return CSV.fromText( text );    
  }

  getValue( index:CSVIndex )
  {
    return this.get( index.line, index.row );
  }

  setValue( index:CSVIndex, value:string )
  {
    this.set( index.line, index.row, value );
  }

  countEmpty( start:CSVIndex, end:CSVIndex )
  {
    return this.count( start, end, v => v === "" );
  }

  count( start:CSVIndex, end:CSVIndex, evaluator:(value:string) => boolean )
  {
    let count = 0;

    this.forAll( start, end, ( v, i ) =>
     {
       if ( evaluator( v ) )
       {
         count ++;
       }
     }
    );

    return count;
  }

  forAllIn( range:CSVRange, action:( value:string, index:CSVIndex )=>void )
  {
    this.forAll( range.start, range.end, action );
  }


  forAll( start:CSVIndex, end:CSVIndex, action:( value:string, index:CSVIndex )=>void )
  {
    let csvIndex = new CSVIndex();

    for ( let i = start.line; i <= end.line; i++ )
    {
      csvIndex.line = i;

      for ( let j = start.row; j <= end.row; j++ )
      {
        csvIndex.row = j;
        let value = this.get( i, j );
        action( value, csvIndex );
      }
    }
  }

  set( lineIndex:number, row:number, value:string )
  {
    this.lines[ lineIndex ][ row ] = value;
  }

  get( lineIndex:number, row:number = 0 )
  {
    if ( lineIndex < 0 || lineIndex >= this.lines.length )
    {
      return null;
    }

    let line = this.getLine( lineIndex );

    if ( row < 0 || row >= line.length )
    {
      return null;
    }

    return line[ row ];
  }

  get numLines(){ return this.lines.length; }

  getLine( line:number ):string[]
  {
    return this.lines[ line ];
  }

  findRowOffset( lineIndex:number, value:string )
  {
    let line = this.getLine( lineIndex );
    return line.indexOf( value );
  }

  find( value:string ):CSVIndex
  {
    for ( let i = 0; i < this.numLines; i++ )
    {
      let line = this.getLine( i );
      let row = line.indexOf( value );

      if ( row != -1 )
      {        
        return CSVIndex.$( i, row );
      }
    }

    return null;
  }

  findEmptyInRow( row:number, lineOffset:number = 0 ):CSVIndex 
  {
    for ( let i = lineOffset; i < this.numLines; i++ )
    {
      let value = this.get( i, row );

      if ( value === "" )
      {
        return CSVIndex.$( i, row );
      }
    }

    return null;
  }

  clearEmpty()
  {
    // console.log( this.values );
    let startLine = -1;
    let endLine   = -1;

    for ( let i = 0; i < this.numLines && startLine === -1; i++ )
    {
      if ( ! this.isLineEmpty( i ) )
      {
        startLine = i;
      }
    }

    for ( let i = this.numLines - 1; i >= 0 && endLine === -1; i-- )
    {
      if ( ! this.isLineEmpty( i ) )
      {
        endLine = i;
      }
    }

    // console.log( { startLine, endLine } );

    let startOffset = 1000000;

    for ( let i = startLine; i <= endLine; i++ )
    {
      let lineOffset = this.indexOfNonEmpty( i );

      if ( lineOffset == -1 )
      {
        continue;
      }

      startOffset = Math.min( startOffset, lineOffset );
    }

    let smallerValues:string[][] = [];

    for ( let i = startLine; i <= endLine; i++ )
    {
      smallerValues.push( this.getSubline( i, startOffset ) );
    }

    this.lines = smallerValues;

  }

  getSubline( lineIndex:number, offset:number )
  {
    let subLine = [];
    let line = this.getLine( lineIndex );

    for ( let i = offset; i < line.length; i++ )
    {
      subLine.push( line[ i ] );
    }

    return subLine;
  }

  indexOfNonEmpty( lineIndex:number )
  {
    let line = this.getLine( lineIndex );

    return line.findIndex( v => v !== "" );
  }

  get nonEmptyLineIndex()
  {
    for ( let i = 0; i < this.numLines; i++ )
    {
      if ( ! this.isLineEmpty( i ) )
      {
        return i;
      }
    }

    return -1;
  }

  get lastNonEmptyLineIndex()
  {
    for ( let i = this.numLines - 1; i >= 0; i-- )
    {
      if ( ! this.isLineEmpty( i ) )
      {
        return i;
      }
    }

    return -1;
  }


  isLineEmpty( lineIndex:number )
  {
    let line = this.getLine( lineIndex );

    let notEmpty = line.find( l => l !== "" );

    if ( notEmpty )
    {
      return false;
    }

    return true;
  }

  read( text:string )
  {
    let lexer = new CSVLexer();
    let tokens = lexer.lexToList( text );
    

    let compressTypes = new Set<string>();
    compressTypes.add(  LexerMatcherLibrary.ANY_SYMBOL_MATCHER.type );
    
    tokens = lexer.compress( tokens, compressTypes );

    
    // console.log( tokens );
    lexer.resolveMatches( text, tokens );

    //console.log( tokens );

    let currentLine = [];
    let currentValue = "";


    for ( let i = 0; i < tokens.length; i++ )
    {
      let token = tokens[ i ];

      if ( token.isError )
      {
        this._error = true;
        console.error( "Lexing error:", token.offset, token.type, token.match );
        return;
      }

      if ( token.isDone )
      {
        currentLine.push( currentValue );
        this.lines.push( currentLine );
        return;
      }

      if ( token.isType( CSVLexer.COMMA ) )
      {
        currentLine.push( currentValue );
        currentValue = "";
      }
      else if ( token.isType( LexerMatcherLibrary.BREAK_MATCHER.type ) )
      {
        currentLine.push( currentValue );        
        this.lines.push( currentLine );
        currentLine = [];
        currentValue = "";
      }
      else if ( token.isType( LexerMatcherLibrary.DOUBLE_QUOTED_STRING_MATCHER.type ) )
      {
        currentValue = CSV.unescapeText( token.match.substring( 1, token.match.length -2 ) );
      }      
      else if ( token.isType( LexerMatcherLibrary.ANY_SYMBOL_MATCHER.type ) )
      {
        currentValue = token.match;
      }

    }
  }


}